/**
 * Tag writes land in Azure at once, but the scorecard only re-reads Azure every
 * few hours, so a fixed check keeps failing until then. This browser-local
 * record of recent fixes lets the UI say so instead of offering the same fix
 * again. It is a hint, not a source of truth: storage may be unavailable, and
 * entries expire once the scorecard has had time to catch up.
 */
const STORAGE_KEY = 'scorecard.resourceGroupTagFix.recent';
/** Fired on `window` whenever this tab records fixes. */
const CHANGE_EVENT = 'scorecard:resource-group-tag-fixes';

/** Well over two scorecard refresh cycles, leaving room for a late one. */
export const RECENT_FIX_TTL_MS = 8 * 60 * 60 * 1000;

type RecentFixes = Record<string, number>;

const entryKey = (tagKey: string, resourceGroupId: string) =>
  `${tagKey}|${resourceGroupId}`.toLowerCase();

/** ARM id in the normalised form the record is keyed by. */
export const resourceGroupArmId = (subscriptionId: string, name: string) =>
  `/subscriptions/${subscriptionId}/resourcegroups/${name}`.toLowerCase();

const read = (now: number): RecentFixes => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '{}');
    if (!parsed || typeof parsed !== 'object') return {};
    return Object.fromEntries(
      Object.entries(parsed).filter(
        (entry): entry is [string, number] =>
          typeof entry[1] === 'number' && now - entry[1] < RECENT_FIX_TTL_MS,
      ),
    );
  } catch {
    return {};
  }
};

const write = (fixes: RecentFixes) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(fixes));
  } catch {
    // Without storage the UI simply forgets; nothing else depends on it.
  }
};

export const recordTagFixes = (
  tagKey: string,
  resourceGroupIds: string[],
  now = Date.now(),
) => {
  if (!resourceGroupIds.length) return;
  const fixes = read(now);
  for (const id of resourceGroupIds) fixes[entryKey(tagKey, id)] = now;
  write(fixes);
  window.dispatchEvent(new Event(CHANGE_EVENT));
};

/**
 * Calls `listener` whenever fixes are recorded, here or in another tab, so every
 * view of them stays in step with the dialog. Returns the unsubscribe.
 */
export const onRecentTagFixesChange = (listener: () => void): (() => void) => {
  const onStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY) listener();
  };
  window.addEventListener(CHANGE_EVENT, listener);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, listener);
    window.removeEventListener('storage', onStorage);
  };
};

/** When each resource group was last fixed for `tagKey`, by normalised ARM id. */
export const getRecentTagFixes = (
  tagKey: string,
  now = Date.now(),
): Map<string, number> => {
  const prefix = entryKey(tagKey, '');
  return new Map(
    Object.entries(read(now))
      .filter(([key]) => key.startsWith(prefix))
      .map(([key, appliedAt]) => [key.slice(prefix.length), appliedAt]),
  );
};
